import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, Eye, CheckCircle, XCircle, LogIn, LogOut, Mail, Loader2, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Reservations = () => {
  const [reservations, setReservations] = useState([]);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const { user } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);

  // Edit Feature States
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  useEffect(() => {
    if (showDetailModal) {
      setIsEditing(false); // Reset edit mode when modal opens
    }
  }, [showDetailModal]);

  useEffect(() => {
    fetchReservations();
  }, []);

  useEffect(() => {
    filterReservations();
  }, [reservations, searchQuery, statusFilter]);

  const fetchReservations = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/reservations`);
      setReservations(response.data);
    } catch (error) {
      toast.error('Error fetching reservations');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteReservation = async (reservationId) => {
    if (!window.confirm('APAKAH ANDA YAKIN? Data reservasi ini akan dihapus PERMANEN dan tidak bisa dikembalikan.')) {
      return;
    }

    setIsDeleting(true);
    try {
      await axios.delete(`${API_URL}/reservations/${reservationId}`);
      toast.success('Reservasi berhasil dihapus permanen');
      fetchReservations(); // Refresh list
    } catch (error) {
      toast.error('Gagal menghapus reservasi: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setIsDeleting(false);
    }
  };

  const filterReservations = () => {
    let filtered = [...reservations];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.guest_name.toLowerCase().includes(query) ||
        r.booking_code.toLowerCase().includes(query) ||
        r.guest_email.toLowerCase().includes(query)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.status === statusFilter);
    }

    setFilteredReservations(filtered);
  };

  const updateStatus = async (reservationId, newStatus) => {
    try {
      await axios.put(`${API_URL}/admin/reservations/${reservationId}/status?status=${newStatus}`);
      toast.success('Status updated');
      fetchReservations();
      setShowDetailModal(false);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const resendEmail = async (reservationId) => {
    setIsSendingEmail(true);
    try {
      await axios.post(`${API_URL}/admin/reservations/${reservationId}/resend-email`);
      toast.success('Email diterima oleh provider');
    } catch (error) {
      toast.error('Gagal mengirim email: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setIsSendingEmail(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'checked_in': return 'bg-emerald-100 text-emerald-800';
      case 'checked_out': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <CheckCircle className="w-4 h-4" />;
      case 'checked_in': return <LogIn className="w-4 h-4" />;
      case 'checked_out': return <LogOut className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    );
  }



  const handleEditClick = () => {
    setEditFormData({ ...selectedReservation });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData({});
  };

  const handleCreateChange = (e, field) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleSaveChanges = async () => {
    try {
      // Validate or transform if needed
      await axios.put(`${API_URL}/admin/reservations/${selectedReservation.reservation_id}`, {
        guest_name: editFormData.guest_name,
        guest_email: editFormData.guest_email,
        guest_phone: editFormData.guest_phone,
        check_in: editFormData.check_in,
        check_out: editFormData.check_out,
        guests: parseInt(editFormData.guests),
        num_rooms: parseInt(editFormData.num_rooms) || 1,
        nights: parseInt(editFormData.nights),
        special_requests: editFormData.special_requests,
        total_amount: parseInt(editFormData.total_amount)
      });

      toast.success('Reservation details updated');
      setIsEditing(false);
      fetchReservations(); // Refresh list to show new data
      setShowDetailModal(false);
    } catch (error) {
      toast.error('Failed to update reservation: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  return (
    <div data-testid="reservations-page">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-gray-900">Daftar Reservasi</h1>
        <p className="text-gray-500">Kelola semua reservasi hotel</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            type="text"
            placeholder="Cari nama, kode booking, atau email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-reservations"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="status-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="checked_in">Checked In</SelectItem>
            <SelectItem value="checked_out">Checked Out</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Kode Booking</TableHead>
              <TableHead>Tamu</TableHead>
              <TableHead>Tipe Kamar</TableHead>
              <TableHead>Check-in/out</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredReservations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  Tidak ada reservasi ditemukan
                </TableCell>
              </TableRow>
            ) : (
              filteredReservations.map((reservation) => (
                <TableRow key={reservation.reservation_id} data-testid={`reservation-row-${reservation.reservation_id}`}>
                  <TableCell className="font-mono text-sm font-medium">
                    {reservation.booking_code}
                    {reservation.requires_siteminder_manual_sync && (
                      <span className="block mt-1 w-fit rounded bg-amber-100 px-2 py-0.5 text-[10px] font-sans font-semibold text-amber-800">
                        Input manual ke CM
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium text-gray-900">{reservation.guest_name}</p>
                      <p className="text-sm text-gray-500">{reservation.guest_email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {reservation.room_type_name}
                    {reservation.num_rooms > 1 && (
                      <span className="ml-1 text-xs text-emerald-600 font-semibold">x{reservation.num_rooms}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <p>{format(new Date(reservation.check_in), 'dd MMM')}</p>
                      <p className="text-gray-500">{format(new Date(reservation.check_out), 'dd MMM')}</p>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium text-emerald-600">
                    Rp {reservation.total_amount.toLocaleString('id-ID')}
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(reservation.status)}`}>
                      {getStatusIcon(reservation.status)}
                      {reservation.status.replace('_', ' ')}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {/* Super Admin Delete Button */}
                      {user?.role === 'superadmin' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteReservation(reservation.reservation_id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          title="Hapus Permanen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedReservation(reservation);
                          setShowDetailModal(true);
                        }}
                        data-testid={`view-reservation-${reservation.reservation_id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detail Reservasi</DialogTitle>
          </DialogHeader>

          {selectedReservation && (
            <div className="space-y-6">
              <div className="flex justify-between items-center bg-emerald-50 rounded-lg p-4">
                <div>
                  <p className="text-sm text-emerald-600">Kode Booking</p>
                  <p className="text-xl font-bold text-emerald-800">{selectedReservation.booking_code}</p>
                </div>
                {!isEditing ? (
                  <Button variant="outline" size="sm" onClick={handleEditClick}>Edit Data</Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={handleCancelEdit}>Batal</Button>
                    <Button size="sm" onClick={handleSaveChanges} className="bg-emerald-600 text-white">Simpan</Button>
                  </div>
                )}
              </div>

              {isEditing ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="text-xs text-gray-500">Nama Tamu</label>
                    <Input value={editFormData.guest_name} onChange={(e) => handleCreateChange(e, 'guest_name')} />
                  </div>
                  <div className="col-span-1">
                    <label className="text-xs text-gray-500">Email</label>
                    <Input value={editFormData.guest_email} onChange={(e) => handleCreateChange(e, 'guest_email')} />
                  </div>
                  <div className="col-span-1">
                    <label className="text-xs text-gray-500">Telepon</label>
                    <Input value={editFormData.guest_phone} onChange={(e) => handleCreateChange(e, 'guest_phone')} />
                  </div>
                  <div className="col-span-1">
                    <label className="text-xs text-gray-500">Check-in</label>
                    <Input type="date" value={editFormData.check_in} onChange={(e) => handleCreateChange(e, 'check_in')} />
                  </div>
                  <div className="col-span-1">
                    <label className="text-xs text-gray-500">Check-out</label>
                    <Input type="date" value={editFormData.check_out} onChange={(e) => handleCreateChange(e, 'check_out')} />
                  </div>
                  <div className="col-span-1">
                    <label className="text-xs text-gray-500">Jumlah Tamu</label>
                    <Input type="number" value={editFormData.guests} onChange={(e) => handleCreateChange(e, 'guests')} />
                  </div>
                  <div className="col-span-1">
                    <label className="text-xs text-gray-500">Jumlah Kamar</label>
                    <Input type="number" min="1" value={editFormData.num_rooms || 1} onChange={(e) => handleCreateChange(e, 'num_rooms')} />
                  </div>
                  <div className="col-span-1">
                    <label className="text-xs text-gray-500">Malam</label>
                    <Input type="number" value={editFormData.nights} onChange={(e) => handleCreateChange(e, 'nights')} />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-gray-500">Permintaan Khusus</label>
                    <Input value={editFormData.special_requests} onChange={(e) => handleCreateChange(e, 'special_requests')} />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-gray-500 font-bold">Total Amount (Override)</label>
                    <Input type="number" value={editFormData.total_amount} onChange={(e) => handleCreateChange(e, 'total_amount')} />
                    <p className="text-[10px] text-gray-400 mt-1">Ubah manual jika ada koreksi harga.</p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Nama Tamu</p>
                    <p className="font-medium">{selectedReservation.guest_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{selectedReservation.guest_email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Telepon</p>
                    <p className="font-medium">{selectedReservation.guest_phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Tipe Kamar</p>
                    <p className="font-medium">{selectedReservation.room_type_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Check-in</p>
                    <p className="font-medium">{format(new Date(selectedReservation.check_in), 'dd MMM yyyy')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Check-out</p>
                    <p className="font-medium">{format(new Date(selectedReservation.check_out), 'dd MMM yyyy')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Jumlah Tamu</p>
                    <p className="font-medium">{selectedReservation.guests} orang</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Jumlah Kamar</p>
                    <p className="font-medium">
                      {selectedReservation.num_rooms || 1} kamar
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Jumlah Malam</p>
                    <p className="font-medium">{selectedReservation.nights} malam</p>
                  </div>
                  {selectedReservation.special_requests && (
                    <div className="col-span-2">
                      <p className="text-sm text-gray-500">Permintaan Khusus</p>
                      <p className="font-medium">{selectedReservation.special_requests}</p>
                    </div>
                  )}
                </div>
              )}

              {!isEditing && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total</span>
                    <span className="text-2xl font-bold text-emerald-600">
                      Rp {selectedReservation.total_amount.toLocaleString('id-ID')}
                    </span>
                  </div>
                  {selectedReservation.discount_amount > 0 && (
                    <p className="text-sm text-emerald-500 text-right">
                      Diskon: Rp {selectedReservation.discount_amount.toLocaleString('id-ID')}
                    </p>
                  )}
                </div>
              )}

              <div>
                <p className="text-sm text-gray-500 mb-2">Ubah Status</p>
                <div className="flex flex-wrap gap-2">
                  {['pending', 'confirmed', 'checked_in', 'checked_out', 'cancelled'].map((status) => (
                    <Button
                      key={status}
                      variant={selectedReservation.status === status ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => updateStatus(selectedReservation.reservation_id, status)}
                      className={selectedReservation.status === status ? 'bg-emerald-600' : ''}
                      data-testid={`status-btn-${status}`}
                    >
                      {status.replace('_', ' ')}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100">
                <Button
                  variant="outline"
                  onClick={() => resendEmail(selectedReservation.reservation_id)}
                  disabled={isSendingEmail}
                  className="w-full"
                  data-testid="resend-email-btn"
                >
                  {isSendingEmail ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  {isSendingEmail ? 'Mengirim...' : 'Kirim Ulang Email Konfirmasi'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reservations;
